import React, { useState } from "react";
import { FormProvider } from "react-hook-form";
import { Box, Paper, Tab, Tabs } from "@mui/material";
import {
  AnamneseSection,
  PhysicalExamSection,
  VitalSignsSection,
  ObservationsSection,
  DiagnosticSuspicionsSection,
  ClinicalConductsSection,
} from "./index";
import {
  Assignment,
  FitnessCenter,
  LocalHospital,
  MonitorHeart,
  Psychology,
  MedicalServices,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente da primeira etapa: Coleta de dados (Atendimento)
 * Gerencia múltiplas seções com formulário e histórico usando tabs
 * Usa react-hook-form do contexto para manter dados persistentes
 *
 * Estrutura das tabs:
 * - Tab 0: Anamnese e Histórico (queixa principal + histórico clínico)
 * - Tab 1: Observações (sistemas corporais + observações gerais)
 * - Tab 2: Exame Físico (linfonodos, ausculta, pele, locomotor, etc)
 * - Tab 3: Sinais Vitais (FC, FR, TPC, temperatura, peso, hidratação)
 * - Tab 4: Suspeitas Diagnósticas (múltiplas suspeitas diagnósticas)
 * - Tab 5: Condutas Clínicas (múltiplas condutas clínicas)
 */
export const DataCollectionStep: React.FC = () => {
  // Busca os métodos do formulário do contexto
  const { methods } = useVetAttendance();
  const { control, handleSubmit, watch, setValue } = methods;

  // Estado para controlar qual tab está ativa
  const [activeTab, setActiveTab] = useState(0);

  /**
   * Handler para mudança de tab
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /**
   * Handler de submissão do formulário (para teste)
   */
  const onSubmit = (data: any) => {
    console.log("Dados coletados:", data);
    // Os dados já estão sendo gerenciados pelo contexto
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ width: "100%" }}
      >
        <Paper sx={{ width: "100%", minHeight: 550 }}>
          {/* Tabs de navegação entre seções */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 64,
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 500,
              },
              "& .Mui-selected": {
                color: "#00A884 !important",
                fontWeight: 600,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#00A884",
                height: 3,
              },
            }}
          >
            <Tab
              icon={<Assignment />}
              iconPosition="start"
              label="Anamnese e Histórico"
            />
            <Tab
              icon={<LocalHospital />}
              iconPosition="start"
              label="Observações"
            />
            <Tab
              icon={<FitnessCenter />}
              iconPosition="start"
              label="Exame Físico"
            />
            <Tab
              icon={<MonitorHeart />}
              iconPosition="start"
              label="Sinais Vitais"
            />
            <Tab
              icon={<Psychology />}
              iconPosition="start"
              label="Suspeitas Diagnósticas"
            />
            <Tab
              icon={<MedicalServices />}
              iconPosition="start"
              label="Condutas Clínicas"
            />
          </Tabs>

          {/* Conteúdo da tab ativa */}
          <Box sx={{ p: 3, minHeight: 470 }}>
            {/* Tab 0: Anamnese e Histórico */}
            {activeTab === 0 && <AnamneseSection control={control} />}

            {/* Tab 1: Observações */}
            {activeTab === 1 && <ObservationsSection control={control} />}

            {/* Tab 2: Exame Físico */}
            {activeTab === 2 && <PhysicalExamSection control={control} />}

            {/* Tab 3: Sinais Vitais */}
            {activeTab === 3 && <VitalSignsSection control={control} />}

            {/* Tab 4: Suspeitas Diagnósticas */}
            {activeTab === 4 && (
              <DiagnosticSuspicionsSection
                control={control}
                watch={watch}
                setValue={setValue}
              />
            )}

            {/* Tab 5: Condutas Clínicas */}
            {activeTab === 5 && (
              <ClinicalConductsSection
                control={control}
                watch={watch}
                setValue={setValue}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </FormProvider>
  );
};

export default DataCollectionStep;
